// A13 — Push Notifications broadcast. TVP2 §9 styling: bordered Paper
// card, MUI standard inputs, Bootstrap-blue Send / grey Discard.
//
// Push platform constraints (kept from earlier sub-plan 07/13 spec):
//   - title  ≤ 60 chars
//   - body   ≤ 178 chars (iOS APNs hard cap on visible text)
//   - kind: promotion (default priority) | system (priority=10, wakes device)
//
// Confirm-broadcast modal pulls recipient count from
// /v1/admin/notifications/recipient-count BEFORE POST.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";

const USER_GROUPS = [
  "Primary",
  "Secondary",
  "All Engineers",
  "All Customers",
  "All",
];

// Always submit `promotion` (server schema accepts it as the default
// anyway). High-priority 'system' broadcasts wake the device immediately
// — that's a rare action and deserves its own dedicated workflow rather
// than a hidden dropdown on the routine broadcast page.
const KIND: "promotion" = "promotion";

interface RecipientCountResp {
  count: number;
}

interface BroadcastResp {
  ok: boolean;
  recipient_count: number;
  status: string;
}

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "too_many_requests":
      return "Daily broadcast quota reached. Try again tomorrow.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

export function PushNotifications() {
  const { show, ToastEl } = useToast();
  const [userGroup, setUserGroup] = useState("Primary");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const recipientCount = useQuery<RecipientCountResp>({
    queryKey: ["admin", "recipient-count", userGroup],
    queryFn: () =>
      apiFetch<RecipientCountResp>(
        `/v1/admin/notifications/recipient-count?user_group=${encodeURIComponent(userGroup)}`,
      ),
    enabled: confirmOpen,
  });

  const send = useMutation({
    mutationFn: () =>
      apiFetch<BroadcastResp>("/v1/admin/notifications/push", {
        method: "POST",
        body: { user_group: userGroup, kind: KIND, title, body },
      }),
    onSuccess: (res) => {
      show(
        `Broadcast queued — delivery in progress (${res.recipient_count} recipients).`,
        "success",
      );
      setTitle("");
      setBody("");
      setConfirmOpen(false);
    },
    onError: (err) => {
      show(friendlyError(err, "Failed to queue broadcast"), "error");
      setConfirmOpen(false);
    },
  });

  const sendDisabled =
    !userGroup || !title.trim() || !body.trim() || send.isPending;

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 3,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={sectionHeading}>Notifications</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <TextField
            variant="standard"
            fullWidth
            required
            select
            label="Choose User Group"
            value={userGroup}
            onChange={(e) => setUserGroup(e.target.value)}
          >
            {USER_GROUPS.map((g) => (
              <MenuItem key={g} value={g}>
                {g}
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box sx={{ mb: 3 }}>
          <TextField
            variant="standard"
            fullWidth
            required
            label="Subject"
            placeholder="Type here..."
            inputProps={{ maxLength: 60 }}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            helperText={`${title.length}/60`}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ mb: 2 }}>
          <TextField
            variant="standard"
            fullWidth
            required
            multiline
            minRows={5}
            label="Content"
            placeholder="Content"
            inputProps={{ maxLength: 178 }}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            helperText={`${body.length}/178`}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={sendDisabled}
            sx={primaryBtn}
            onClick={() => setConfirmOpen(true)}
          >
            Send
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            onClick={() => {
              setTitle("");
              setBody("");
            }}
          >
            Discard
          </Button>
        </Stack>
      </Paper>

      <ConfirmModal
        open={confirmOpen}
        title="Confirm broadcast"
        body={
          <Typography>
            About to send a push notification to{" "}
            <strong>
              {recipientCount.isLoading
                ? "…"
                : recipientCount.data?.count ?? "?"}
            </strong>{" "}
            <strong>{userGroup}</strong> recipients. This action cannot be
            undone. Continue?
          </Typography>
        }
        confirmLabel="Send"
        pending={send.isPending}
        onConfirm={() => send.mutate()}
        onCancel={() => setConfirmOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
